import { useAppDispatch, useAppSelector } from 'app/state'
import {
  completeOnboardingChecklistRequest,
  setOnboardingChecklistOpen
} from 'features/onboarding-checklist/state/onboardingChecklistSlice'
import { createMetricRequest } from 'features/metrics/state/metricsSlice'
import { MetricEventType } from 'features/metrics/api/metrics'
import useUserRoles from 'features/account/state/useUserRoles'
import { dateIsEqualOrOlderThanNDays } from 'features/common/utils/dateUtils'
import { useMeUser } from 'features/users/hooks/useMeUser'

function useIsUserOnboarding() {
  const completed = useAppSelector((state) => state.onboardingChecklist.completed)
  const showCompletionConfirmation = useAppSelector((state) => state.onboardingChecklist.showCompletionConfirmation)
  const { userIsViewer, userIsGuest } = useUserRoles()
  const user = useMeUser()

  /*
    As of today, only non-viewer users have onboarding checklist with tasks that they can complete...
    Also, beyond 30 days it looks like there is no reason for an 'on-boarding', even if something is configured like so on the backend side
    so we take this simple approach to disable onBoarding UI for those users (viewers or 'old' users)
  */

  const now = new Date()
  const userIsTooOld = dateIsEqualOrOlderThanNDays({
    date: new Date(user?.created_at || now),
    now,
    days: 30
  })

  const shouldShowOnboarding = !completed || showCompletionConfirmation
  const hasBlockerConditions = userIsViewer || userIsTooOld || userIsGuest

  return shouldShowOnboarding && !hasBlockerConditions
}

function useOnboardingChecklist() {
  const isUserOnboarding = useIsUserOnboarding()
  const dispatch = useAppDispatch()
  const open = useAppSelector((state) => state.onboardingChecklist.open)
  const status = useAppSelector((state) => state.onboardingChecklist.status)

  const toggleOnboardingChecklist = () => {
    if (!open) {
      dispatch(createMetricRequest(MetricEventType.OnboardingOpened))
    }
    dispatch(setOnboardingChecklistOpen(!open))
  }

  const completeOnboardingChecklist = (tasksIds: string | string[]) => {
    const tasks = Array.isArray(tasksIds) ? tasksIds : [tasksIds]
    dispatch(completeOnboardingChecklistRequest(tasks))
  }

  const skipOnboarding = () => {
    dispatch(completeOnboardingChecklistRequest('SkipOnBoarding'))
  }
  return {
    onBoardingCheckListStatus: status,
    toggleOnboardingChecklist,
    completeOnboardingChecklist,
    skipOnboarding,
    isUserOnboarding
  }
}

export enum OnboardingAction {
  InviteColleagues = 'invite_your_colleagues', // TODO VVG: replace all onboarding calls, removing magic strings
  BuildFirstMap = 'build_first_map',
  BuildFirstWorkflow = 'build_first_workflow',
  BrowseDataExplorer = 'browse_data_explorer',
  CheckDemoMap = 'check_demo_map',
  BrowseDOCatalog = 'browse_do_catalog',
  WatchWorkspaceVideo = 'watch_initial_workspace_video',
  WatchBuilderVideo = 'watch_initial_builder_video',
  WatchWorkflowsVideo = 'watch_initial_workflows_video',
  WatchAIAgentVideo = 'watch_ai_agent_video'
}

export default useOnboardingChecklist
